import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { AccountCircle, Lock } from "@mui/icons-material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { styled, keyframes, alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import useAdd from "../../../api/useAdd";
import { useAuth } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";
import axios from "axios";

// ---------- Utilities ----------
const convertToEnglishDigits = (text = "") => {
  const easternDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
  let converted = text;
  easternDigits.forEach(
    (d, i) => (converted = converted.replace(new RegExp(d, "g"), String(i)))
  );
  return converted;
};

// ---------- Styled + Motion ----------
const float = keyframes`
  0% { transform: translateY(0px) translateX(0px) scale(1); }
  50% { transform: translateY(-12px) translateX(6px) scale(1.02); }
  100% { transform: translateY(0px) translateX(0px) scale(1); }
`;

const gradientShift = keyframes`
  0% { filter: hue-rotate(0deg); }
  50% { filter: hue-rotate(25deg); }
  100% { filter: hue-rotate(0deg); }
`;

const Canvas = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  width: "100%",
  position: "relative",
  display: "grid",
  placeItems: "center",
  overflow: "hidden",
  background: `radial-gradient(1200px 600px at 80% -10%, ${alpha(
    theme.palette.primary.main,
    theme.palette.mode === "dark" ? 0.25 : 0.15
  )} 0%, transparent 60%),
              radial-gradient(900px 500px at -10% 110%, ${alpha(
                theme.palette.secondary.main,
                theme.palette.mode === "dark" ? 0.18 : 0.12
              )} 0%, transparent 65%),
              linear-gradient(135deg, ${theme.palette.background.default} 0%, ${
    theme.palette.mode === "dark" ? "#0b0f14" : "#f7fafc"
  } 100%)`,
}));

const Blob = styled("div")(({ theme }) => ({
  position: "absolute",
  borderRadius: "50%",
  background: `radial-gradient(circle at 30% 30%, ${alpha(
    theme.palette.primary.light,
    0.6
  )}, ${alpha(theme.palette.primary.main, 0.3)} 50%, transparent 70%)`,
  filter: "blur(20px)",
  animation: `${float} 9s ease-in-out infinite, ${gradientShift} 16s linear infinite`,
  pointerEvents: "none",
  zIndex: 0,
}));

const GlassCard = styled(Card)(({ theme }) => ({
  position: "relative",
  width: "min(92vw, 980px)",
  display: "grid",
  gridTemplateColumns: "1.2fr 1fr",
  gap: 0,
  borderRadius: 28,
  overflow: "hidden",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 20px 50px rgba(0,0,0,0.55)"
      : "0 24px 50px rgba(0,0,0,0.14)",
  background: alpha(theme.palette.background.paper, 0.7),
  backdropFilter: "blur(18px) saturate(120%)",
  border: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
  },
}));

const InfoSide = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.14
  )} 0%, ${alpha(theme.palette.secondary.main, 0.14)} 100%)`,
  borderRight: `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    borderRight: "none",
    borderBottom: `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
  },
}));

const FormSide = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6),
  display: "grid",
  placeItems: "center",
}));

const SectionBadge = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 12px",
  borderRadius: 999,
  fontSize: 12,
  letterSpacing: 0.4,
  fontWeight: 700,
  textTransform: "uppercase",
  color:
    theme.palette.mode === "dark"
      ? theme.palette.grey[200]
      : theme.palette.grey[700],
  background: alpha(theme.palette.primary.main, 0.12),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
}));

const Feature = ({ children }) => (
  <Typography
    variant="body2"
    sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
  >
    <span role="img" aria-hidden>
      •
    </span>{" "}
    {children}
  </Typography>
);

// ---------- Component ----------
function Login() {
  const { t, i18n } = useTranslation();
  const isRtl = useMemo(
    () =>
      (typeof i18n?.dir === "function" && i18n.dir() === "rtl") ||
      document?.documentElement?.dir === "rtl",
    [i18n]
  );
  const auth = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);

  const [formData, setFormData] = useState({ login: "", password: "", selected_branch: "" });
  const [formErrors, setFormErrors] = useState({});
  const [branchAlert, setBranchAlert] = useState("");
  const [branchOptions, setBranchOptions] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [requiresBranch, setRequiresBranch] = useState(true);
  const [branchMessage, setBranchMessage] = useState("");

  const { handleAdd, resetForm, loading, responseData } = useAdd(
    "login",
    "",
    t("user.successFully"),
    t("user.failedLogin")
  );

  useEffect(() => {
    if (resetForm) {
      setFormData({ login: "", password: "", selected_branch: "" });
      auth.login(responseData);
      navigate("/shop/");
      setFormErrors({});
      setBranchOptions([]);
      setRequiresBranch(true);
      setBranchMessage("");
    }
  }, [resetForm, responseData, auth, navigate]);

  useEffect(() => {
    const loginValue = formData.login.trim();
    if (!loginValue) {
      setBranchOptions([]);
      setRequiresBranch(true);
      setBranchMessage("");
      setFormData((prev) => ({ ...prev, selected_branch: "" }));
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setBranchLoading(true);
        const apiBase =
          process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000/api";
        const response = await axios.post(
          `${apiBase}/login-branches/`,
          { login: loginValue }
        );
        const payload = response.data || {};
        const branches = Array.isArray(payload.branches) ? payload.branches : [];
        setBranchOptions(branches);
        setRequiresBranch(payload.requires_branch !== false);
        setBranchMessage(payload.message || "");
        setFormData((prev) => {
          const branchExists = branches.some(
            (branch) => String(branch.id) === String(prev.selected_branch)
          );
          return {
            ...prev,
            selected_branch:
              payload.requires_branch === false
                ? ""
                : branchExists
                  ? prev.selected_branch
                  : branches.length === 1
                    ? String(branches[0].id)
                    : "",
          };
        });
      } catch (error) {
        setBranchOptions([]);
        setRequiresBranch(true);
        setBranchMessage("");
      } finally {
        setBranchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [formData.login]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = {};
    Object.entries(formData).forEach(([k, v]) => {
      if (k !== "selected_branch" && !v) errors[k] = t("user.failedRequired");
    });
    if (requiresBranch && !formData.selected_branch) {
      errors.selected_branch = "Please select a branch.";
      setBranchAlert("Please select a branch to continue.");
    }
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    setBranchAlert("");
    const payload = new FormData();
    Object.entries(formData).forEach(([k, v]) => payload.append(k, v));
    payload.append("remember", remember ? "1" : "0");
    handleAdd(payload);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === "login") v = convertToEnglishDigits(v);
    setFormData((s) => ({ ...s, [name]: v }));
    setFormErrors((s) => ({ ...s, [name]: "" }));
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 14,
      transition: "all .2s ease",
      backdropFilter: "blur(6px)",
      backgroundColor: alpha(theme.palette.background.paper, 0.6),
      "& fieldset": { borderColor: alpha(theme.palette.divider, 0.5) },
      "&:hover fieldset": {
        borderColor: alpha(theme.palette.primary.main, 0.6),
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  };

  return (
    <>
      <CssBaseline />
      <Canvas dir={isRtl ? "rtl" : "ltr"}>
        {/* Ambient blobs (sizes/positions via sx) */}
        <Blob sx={{ width: 420, height: 420, top: "8%", left: "68%" }} />
        <Blob sx={{ width: 320, height: 320, top: "70%", left: "5%" }} />
        <Blob sx={{ width: 260, height: 260, top: "78%", left: "80%" }} />

        <GlassCard
          component={motion.div}
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 80, damping: 16, mass: 0.9 }}
        >
          {/* Left / Info */}
          <InfoSide
            component={motion.div}
            initial={{ opacity: 0, x: isRtl ? 24 : -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.05 }}
          >
            <SectionBadge>
              <span aria-hidden>✨</span> Ploranzai Plus
            </SectionBadge>

            <Typography
              variant={isSm ? "h4" : "h3"}
              sx={{
                mt: 2,
                fontWeight: 900,
                letterSpacing: 0.4,
                textShadow:
                  theme.palette.mode === "dark"
                    ? "0 2px 12px rgba(0,0,0,.6)"
                    : "none",
              }}
            >
              {t("Inventory Management System") ||
                "Inventory Management System"}
            </Typography>

            <Typography
              variant="body1"
              sx={{ mt: 1.5, opacity: 0.85, maxWidth: 540, lineHeight: 1.7 }}
            >
              {t(
                "Modern, powerful, and easy-to-use inventory management for businesses of all sizes."
              ) ||
                "Modern, powerful, and easy-to-use inventory management for businesses of all sizes."}
            </Typography>

            <Box sx={{ mt: 3, display: "grid", gap: 1.1, maxWidth: 560 }}>
              <Typography
                variant="subtitle2"
                sx={{ opacity: 0.7, fontWeight: 700 }}
              >
                {t("Key Features") || "Key Features"}
              </Typography>
              <Feature>Manage multiple shops seamlessly</Feature>
              <Feature>Employee management & roles</Feature>
              <Feature>Product & category management</Feature>
              <Feature>Units & multiple currencies</Feature>
              <Feature>Supplier management</Feature>
              <Feature>Real-time stock & reporting</Feature>
              <Feature>Payroll, purchase, sales, returns, expenses</Feature>
              <Feature>Advanced dashboards & analytics</Feature>
            </Box>

            <Box
              sx={{
                mt: 4,
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
                opacity: 0.9,
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  background: alpha(theme.palette.success.main, 0.15),
                  border: `1px solid ${alpha(
                    theme.palette.success.main,
                    0.35
                  )}`,
                }}
              >
                99.9% Uptime
              </Box>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  background: alpha(theme.palette.info.main, 0.15),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
                }}
              >
                SSO Ready
              </Box>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  background: alpha(theme.palette.warning.main, 0.15),
                  border: `1px solid ${alpha(
                    theme.palette.warning.main,
                    0.35
                  )}`,
                }}
              >
                Role-Based Access
              </Box>
            </Box>
          </InfoSide>

          {/* Right / Form */}
          <FormSide
            component={motion.div}
            initial={{ opacity: 0, x: isRtl ? -24 : 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card
              elevation={0}
              sx={{
                width: "min(100%, 440px)",
                borderRadius: 20,
                p: { xs: 3, md: 4 },
                background: alpha(theme.palette.background.paper, 0.65),
                backdropFilter: "blur(12px)",
                border: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
              }}
              component={motion.div}
              whileHover={{ y: -2 }}
              transition={{ type: "spring", stiffness: 250, damping: 18 }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ textAlign: "center", mb: 2 }}>
                  <Typography
                    component="h1"
                    variant={isSm ? "h5" : "h4"}
                    sx={{ fontWeight: 800, letterSpacing: 0.3 }}
                  >
                    {t("Welcome Back!") || "Welcome Back!"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.75, opacity: 0.7, fontStyle: "italic" }}
                  >
                    {t("Sign in to your account to continue") ||
                      "Sign in to your account to continue"}
                  </Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <TextField
                    fullWidth
                    required
                    id="login"
                    name="login"
                    label={t("user.login")}
                    value={formData.login}
                    onChange={handleChange}
                    error={!!formErrors.login}
                    helperText={formErrors.login}
                    autoComplete="username"
                    autoFocus
                    sx={{ ...inputSx, mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccountCircle />
                        </InputAdornment>
                      ),
                      inputProps: { "aria-label": t("user.login") },
                    }}
                  />

                  <TextField
                    fullWidth
                    required
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    label={t("user.password")}
                    value={formData.password}
                    onChange={handleChange}
                    error={!!formErrors.password}
                    helperText={formErrors.password}
                    autoComplete="current-password"
                    sx={{ ...inputSx }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                            onClick={() => setShowPassword((s) => !s)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {branchAlert ? (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {branchAlert}
                    </Alert>
                  ) : null}

                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={remember}
                          onChange={(e) => setRemember(e.target.checked)}
                          sx={{ p: 0.5 }}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {t("Remember me") || "Remember me"}
                        </Typography>
                      }
                    />
                    {/* <Typography
                      component={Link}
                      to="/forgot-password"
                      style={{ textDecoration: "none" }}
                      color="primary"
                      sx={{ fontSize: 13, fontWeight: 700 }}
                    >
                      {t("Forgot password?") || "Forgot password?"}
                    </Typography> */}
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{
                      mt: 2.5,
                      py: 1.3,
                      borderRadius: 12,
                      fontWeight: 800,
                      letterSpacing: 0.4,
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 10px 28px rgba(0,0,0,.6)"
                          : "0 10px 22px rgba(0,0,0,.15)",
                      transition: "transform .15s ease, box-shadow .15s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 12px 32px rgba(0,0,0,.65)"
                            : "0 14px 30px rgba(0,0,0,.18)",
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={22} thickness={5} />
                    ) : (
                      t("user.signIn")
                    )}
                  </Button>

                  <Divider sx={{ my: 2.5, opacity: 0.5 }} />

                  <Typography
                    variant="caption"
                    sx={{ display: "block", textAlign: "center", opacity: 0.7 }}
                  >
                    {t("Don’t have an account?") || "Don’t have an account?"}{" "}
                    <Typography
                      component={Link}
                      to="https://wa.me/93765169854"
                      target="_blank"
                      color="primary"
                      sx={{ fontWeight: 800, textDecoration: "none" }}
                    >
                      {t("Create one") || "Create one"}
                    </Typography>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </FormSide>
        </GlassCard>
      </Canvas>
    </>
  );
}

export default Login;
